import {useMemo, useState} from 'react'
import {useQuery} from '@apollo/client'
import {
  Box,
  Container,
  Flex,
  FormLabel,
  Grid,
  GridItem,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Text,
} from '@chakra-ui/react'
import {useTranslation} from 'next-i18next'
import Image from 'next/legacy/image'
import {addApolloState} from 'helpers/ApolloHelper/ApolloHelper'
import withAppInit from '_app/hoc/withAppInit'
import {Context} from '_app/types/context'
import Page from '_app/components/Page/Page'
import {GET_SHOWROOMS} from '_app/graphql/queries/content/GetShowrooms'
import Tab from '_app/components/Tab'
import {Showroom} from '_app/graphql/generated/graphql'
import {CloseIcon} from '_app/components/Icons'
import Link from '_app/components/Link/Link'
import I18nHelper from '../../helpers/I18nHelper/I18nHelper'

type Props = {
  hasNoIndex: boolean;
  hasNoFollow: boolean;
};

const BoutiquesPage = ({ hasNoIndex, hasNoFollow }: Props) => {
  const { t } = useTranslation('boutique')
  const { data } = useQuery(GET_SHOWROOMS)
  const tabs: {
    key: 'POLAND' | 'GERMANY' | 'FRANCE';
    label: string;
    image: string;
    domain: string;
    width: number;
  }[] = useMemo(
    () => [
      {
        key: 'POLAND',
        label: t('POLAND'),
        image: '/images/maps/poland2.png',
        width: 492,
        domain: '.pl',
      },
      {
        key: 'GERMANY',
        label: t('GERMANY'),
        image: '/images/maps/deutschland.webp',
        width: 344,
        domain: '.de',
      },
      {
        key: 'FRANCE',
        label: t('FRANCE'),
        image: '/images/maps/france.webp',
        width: 454,
        domain: '.fr',
      },
    ],
    [t]
  )
  const [activeTab, setActiveTab] = useState(tabs[0])
  const itemsLength = data?.showrooms?.items?.[0]?.contact?.email?.[0]
    ? data?.showrooms?.items?.filter((item: any) =>
        item?.contact?.email?.[0]?.includes(activeTab.domain)
      )?.length
    : data?.showrooms?.items?.length

  const filteredData = data?.showrooms?.items?.[0]?.contact?.email?.[0]
    ? data?.showrooms?.items?.filter((item: any) =>
        item?.contact?.email?.[0]?.includes(activeTab.domain)
      )
    : data?.showrooms?.items

  const [searchedCity, setSearchedCity] = useState('')

  const col1: Showroom[] =
    itemsLength >= 16
      ? filteredData?.filter(
          (_: Showroom, index: number) => index < itemsLength / 2
        )
      : filteredData

  const col2: Showroom[] =
    itemsLength >= 16
      ? filteredData?.filter(
          (_: Showroom, index: number) => index >= itemsLength / 2
        )
      : []

  return (
    <Page
      seo={{
        title: t('BOUTIQUES'),
      }}
      hasNoIndex={hasNoIndex}
      hasNoFollow={hasNoFollow}
    >
      <Container
        py={{
          base: '48px',
          lg: '72px',
        }}
      >
        <Flex
          flexDir={{ base: 'column', lg: 'row' }}
          gap={{
            base: '24px',
            lg: '150px',
          }}
        >
          <Box flex="1 1 100%">
            <Text as="h1" textStyle="sectionHeader" mb={6}>
              {t('LETS_MEET')}
            </Text>
            <Text mb="40px" textStyle="bodyLight">
              {t('WE_INVITE_YOU_TO_LILOU_BOUTIQUES')}
            </Text>

            <FormLabel htmlFor="search">{t('SEARCH_FOR_A_CITY')}</FormLabel>
            <InputGroup mb="40px">
              <Input
                value={searchedCity}
                onChange={e => setSearchedCity(e.target.value)}
                p={0}
                name="search"
                maxWidth="930px"
                minWidth="295px"
                placeholder={t('CITY_PLACEHOLDER')}
              />
              <InputRightElement>
                <IconButton
                  borderRadius="4px"
                  _hover={{ bg: '#00000013' }}
                  position="absolute"
                  variant="icon"
                  icon={<CloseIcon />}
                  aria-label={t('DELETE', { ns: 'common' })}
                  onClick={() => setSearchedCity('')}
                />
              </InputRightElement>
            </InputGroup>
            {searchedCity &&
              ![
                ...col1.map(showroom => showroom?.name?.toLowerCase()),
                ...col2.map(showroom => showroom?.name?.toLowerCase()),
              ].some(city => city?.includes(searchedCity?.toLowerCase())) && (
                <Text mb="72px" lineHeight={1.38}>
                  {t('NO_BOUTIQUES_THERE_YET')}
                  <br />
                  {t('USE_ONLINE_STORE')}
                </Text>
              )}

            <Box
              py={4}
              display={{ base: 'none', lg: 'block' }}
              mx="auto"
              position="relative"
              width={activeTab.width ?? 466}
              height={466}
            >
              <Image
                layout="fill"
                alt={`${activeTab.label} map`}
                src={activeTab.image}
                style={{ width: '100%', height: '100%' }}
              />
            </Box>
          </Box>
          <Box flex="1 1 100%">
            <Box mb={{ base: 6, lg: '36px' }} width="100%">
              <Flex
                borderColor="mercury"
                borderTopWidth={1}
                borderBottomWidth={1}
                gap="32px"
                width="full"
                borderTop="1px solid #e5e5e5"
                borderBottom="1px solid #e5e5e5"
                boxShadow="0 10px 15px -10px #e5e5e5"
              >
                {tabs.map(tab => (
                  <Tab
                    key={tab.key}
                    isActive={tab.key === activeTab?.key}
                    onClick={() => setActiveTab(tab)}
                    groupKey=""
                    label={tab.label}
                  />
                ))}
              </Flex>
            </Box>
            <Grid
              templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }}
              gap={{ base: 0, lg: '30px' }}
            >
              <GridItem w="100%" display="flex" flexDir="column" gap={4}>
                {col1?.map((showroom: Showroom) => (
                  <Text
                    _hover={{
                      textDecoration: 'underline',
                    }}
                    key={showroom.id}
                    position="relative"
                    _after={
                      searchedCity &&
                      !showroom?.name
                        ?.toLowerCase()
                        ?.includes(searchedCity?.toLowerCase())
                        ? {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            bg: 'white',
                            opacity: 0.5,
                            zIndex: 1,
                          }
                        : {}
                    }
                  >
                    <Link href={`/showrooms/${showroom.id}` ?? '/'} passHref>
                      {showroom.name}
                    </Link>
                  </Text>
                ))}
              </GridItem>

              <GridItem w="100%" display="flex" flexDir="column" gap={4} mt={4}>
                {col2?.map((showroom: Showroom) => (
                  <Text
                    _hover={{
                      textDecoration: 'underline',
                    }}
                    key={showroom.id}
                    position="relative"
                    _after={
                      searchedCity &&
                      !showroom?.name
                        ?.toLowerCase()
                        ?.includes(searchedCity?.toLowerCase())
                        ? {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            bg: 'white',
                            opacity: 0.5,
                            zIndex: 1,
                          }
                        : {}
                    }
                  >
                    <Link href={`/showrooms/${showroom.id}` ?? '/'}>
                      {showroom.name}
                    </Link>
                  </Text>
                ))}
              </GridItem>
            </Grid>
          </Box>
        </Flex>
      </Container>
    </Page>
  )
}

export const getServerSideProps = withAppInit(async (ctx: Context) => {
  return addApolloState(ctx.apolloClient, {
    props: {
      ...(await I18nHelper.getI18nProps(ctx, ['boutique'])),
    },
  })
})

export default BoutiquesPage
